<template>
  <BInput
    :id="id"
    ref="autocomplete"
    v-model="autocompleteText"
    class="ls-google-autocomplete"
    :placeholder="placeholder"
    icon="replacePin"
    test-id="ls-google-autocomplete"
    @focus="onFocus"
    @change.native="onChange"
  />
</template>

<script>
import VueTypes from 'vue-types';
import { lsAddressHelpers } from 'ls-vue-common';
import _ from 'lodash';

export default {
  name: 'LsGoogleAutocomplete',

  props: {
    id: VueTypes.string.def('autocomplete'),
    placeholder: VueTypes.string.def('Enter your address'),
    enableGeolocation: VueTypes.bool.def(true),
  },

  data() {
    return {
      /**
       * The Autocomplete object.
       *
       * @type {Autocomplete}
       * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
       */
      autocomplete: null,

      /**
       * Autocomplete input text
       * @type {String}
       */
      autocompleteText: '',

      geolocation: {
        /**
         * Google Geocoder Objet
         * @type {Geocoder}
         * @link https://developers.google.com/maps/documentation/javascript/reference#Geocoder
         */
        geocoder: null,

        /**
         * Filled after geolocate result
         * @type {Coordinates}
         * @link https://developer.mozilla.org/en-US/docs/Web/API/Coordinates
         */
        loc: null,

        /**
         * Filled after geolocate result
         * @type {Position}
         * @link https://developer.mozilla.org/en-US/docs/Web/API/Position
         */
        position: null,
      },
    };
  },

  watch: {
    autocompleteText(newVal, oldVal) {
      this.$emit('inputChange', { newVal, oldVal }, this.id);
    },
  },

  mounted() {
    const options = {
      types: ['address'],
      componentRestrictions: {
        country: 'us',
      },
    };

    const input = document.getElementById(this.id);
    // eslint-disable-next-line no-undef
    this.autocomplete = new google.maps.places.Autocomplete(input, options);
    this.autocomplete.addListener('place_changed', this.onPlaceChanged);
  },

  methods: {
    /**
     * When a place changed
     */
    onPlaceChanged() {
      let place = this.autocomplete.getPlace();

      if (!place.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.
        this.$emit('no-results-found', place, this.id);
        return;
      }

      if (place.address_components !== undefined) {
        // return returnData object and PlaceResult object
        this.$emit('placechanged', lsAddressHelpers.formatPlace({ place }), place, this.id);

        // update autocompleteText then emit change event
        this.autocompleteText = _.get(document.getElementById(this.id), 'value');
        this.onChange();
      }
    },

    /**
     * When the input gets focus
     */
    onFocus() {
      this.biasAutocompleteLocation();
      this.$emit('focus');
    },

    /**
     * When the input got changed
     */
    onChange() {
      this.$emit('change', this.autocompleteText);
    },

    /**
     * Clear the input
     */
    clear() {
      this.autocompleteText = '';
    },

    /**
     * Focus the input
     */
    focus() {
      this.$refs.autocomplete.focus();
    },

    /**
     * Update internal location from navigator geolocation
     * @param  {Function} (geolocation, position)
     */
    updateGeolocation(callback = null) {
      if (navigator.geolocation) {
        let options = {};
        navigator.geolocation.getCurrentPosition(
          (position) => {
            let geolocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            this.geolocation.loc = geolocation;
            this.geolocation.position = position;

            if (callback) callback(geolocation, position);
          },
          (err) => {
            this.$emit('error', 'Cannot get Coordinates from navigator', err);
          },
          options
        );
      }
    },

    // Bias the autocomplete object to the user's geographical location,
    // as supplied by the browser's 'navigator.geolocation' object.
    biasAutocompleteLocation() {
      if (this.enableGeolocation) {
        this.updateGeolocation((geolocation, position) => {
          // eslint-disable-next-line no-undef
          let circle = new google.maps.Circle({
            center: geolocation,
            radius: position.coords.accuracy,
          });
          this.autocomplete.setBounds(circle.getBounds());
        });
      }
    },
  },
};
</script>

<style lang="scss">
.ls-google-autocomplete {
  .mdi-replacePin::after {
    content: url(pin.svg);
    position: absolute;
    top: 7px;
    right: 8px;
  }
}

.pac-container {
  position: fixed !important;
}

.themed-app {
  .mdi-replacePin::after {
    background-repeat: no-repeat;
    background-color: $theme-color-primary;
    content: '';
    height: 16px;
    mask: url(pin.svg);
    width: 16px;
  }
}
</style>
